import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationPreferenceChannel } from '__generated__/globalTypes';
import { useFeatureFlags } from 'hooks/useFeatureFlags';

import { NotificationPreferenceLayout } from '../NotificationPreferenceLayout';
import { notificationSubtitles, notificationTitles } from '../messages';
import { DisplayablePreference } from '../types';
import { BooleanSelector } from './BooleanSelector';
import { DropdownSelector } from './DropdownSelector';
import { PreferenceToggleButton } from './PreferenceToggleButton';
import type { NotificationPreference_notificationPreference } from './__generated__/index.graphql';

type Props = {
  preference: NotificationPreference_notificationPreference;
  // TODO: Remove JSON when switching to new notification preferences
  onChange: (channels: NotificationPreferenceChannel[] | Json) => void;
};

export const NotificationPreference = ({ preference, onChange }: Props) => {
  const {
    flags: { useNewNotificationPreferences = false },
  } = useFeatureFlags();

  const { name, value, defaultValue, title, description } = preference;

  if (useNewNotificationPreferences) {
    return (
      <NotificationPreferenceLayout title={title} description={description}>
        <PreferenceToggleButton
          notificationPreference={preference}
          type="email"
          onChange={onChange}
        />
        <PreferenceToggleButton
          notificationPreference={preference}
          type="inAppPush"
          onChange={onChange}
        />
      </NotificationPreferenceLayout>
    );
  }

  const currentValue = value ?? defaultValue;

  let controlComponent: ReactNode;
  if (preference.labels?.length) {
    controlComponent = (
      <DropdownSelector preference={preference} handleChange={onChange} />
    );
  } else {
    controlComponent = (
      <BooleanSelector currentValue={currentValue} handleChange={onChange} />
    );
  }

  return (
    <NotificationPreferenceLayout
      title={
        <FormattedMessage
          {...notificationTitles[name as DisplayablePreference]}
        />
      }
      description={
        <FormattedMessage
          {...notificationSubtitles[name as DisplayablePreference]}
        />
      }
    >
      {controlComponent}
    </NotificationPreferenceLayout>
  );
};

NotificationPreference.fragments = {
  notificationPreference: gql`
    fragment NotificationPreference_notificationPreference on NotificationPreference {
      name
      value
      defaultValue
      title
      description
      ...DropdownSelector_notificationPreference
      ...PreferenceToggleButton_notificationPreference
    }
    ${DropdownSelector.fragments.notificationPreference}
    ${PreferenceToggleButton.fragments.notificationPreference}
  ` as TypedDocumentNode<NotificationPreference_notificationPreference>,
};
